/**
 * Canguaru search (search round) · client-side search over the theme's own
 * listing index (/pages/search-index JSON, section canguaru-search-index).
 * Replaces Shopify's native search end to end: nothing here calls
 * search.results or /search/suggest.json.
 *
 * Two consumers, one engine:
 *  - the /search page shell (sections/canguaru-search.liquid, [data-cg-search])
 *  - the header popup (snippets/canguaru-search-modal.liquid, [data-cg-search-popup])
 *
 * Index cache: sessionStorage, 1h TTL, keyed on settings.search_index_version
 * so the editor can bust it without code.
 */

/* ---------------------------------------------------------------- synonyms */

// Expanded in both directions before matching: a query token belonging to a
// group matches any token of that group. Multi-word phrases are stored as
// their normalised tokens. Grown from zero-result logs (cg-search-zero).
const SYN_GROUPS = [
  ['removalist', 'removalists', 'removals', 'moving', 'mudanca', 'mudancas'],
  ['dentist', 'dentista'],
  ['hairdresser', 'cabeleireira', 'cabeleireiro', 'hair', 'salon', 'salao'],
  ['bbq', 'churrasco', 'churrascaria'],
  ['accountant', 'accounting', 'contador', 'contadora', 'contabilidade'],
  ['lawyer', 'advogado', 'advogada'],
  ['cleaner', 'cleaning', 'faxina', 'limpeza'],
  ['beautician', 'beauty', 'estetica'],
  ['butcher', 'acougue'],
  ['bakery', 'padaria', 'bolos', 'cake', 'cakes'],
  ['cafe', 'coffee', 'cafeteria'],
  ['grocery', 'mercado', 'mercadinho', 'supermarket'],
  // capoeira / samba / forro / zouk stay as-is (diacritics normalise away);
  // jiu jitsu misspellings meet here.
  ['jiujitsu', 'jiu', 'jitsu', 'bjj'],
];

const ALIASES = (() => {
  const map = new Map();
  for (const group of SYN_GROUPS) {
    for (const token of group) {
      const set = map.get(token) || new Set();
      for (const other of group) set.add(other);
      map.set(token, set);
    }
  }
  return map;
})();

/* ------------------------------------------------------------ normalising */

export function normalise(str) {
  return String(str || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function tokenize(str) {
  const n = normalise(str);
  return n ? n.split(' ') : [];
}

/* ------------------------------------------------------------------ index */

const INDEX_CACHE_PREFIX = 'cg-search-index:';
const INDEX_TTL_MS = 60 * 60 * 1000; // 1 hour
const MAX_PAGES = 40;

let indexPromise = null;

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !Array.isArray(data.items)) return null;
    if (Date.now() - data.t > INDEX_TTL_MS) return null;
    return data.items;
  } catch (e) {
    return null;
  }
}

function writeCache(key, items) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), items }));
  } catch (e) {
    /* quota/private mode: run uncached */
  }
}

async function fetchPage(indexUrl, page) {
  const sep = indexUrl.includes('?') ? '&' : '?';
  const res = await fetch(`${indexUrl}${sep}page=${page}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`cg-search index HTTP ${res.status}`);
  const text = await res.text();
  // The section output rides inside Shopify's section wrapper div — slice
  // from the first "{" to the last "}" before parsing.
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start === -1 || end === -1) throw new Error('cg-search index: no JSON found');
  return JSON.parse(text.slice(start, end + 1));
}

/**
 * Load the whole index (all pages), cached per version for 1h.
 * @param {{indexUrl: string, version: string}} cfg
 */
export function loadIndex(cfg) {
  if (indexPromise) return indexPromise;
  const key = INDEX_CACHE_PREFIX + (cfg.version || '1');
  const cached = readCache(key);
  if (cached) {
    indexPromise = Promise.resolve(cached);
    return indexPromise;
  }
  indexPromise = (async () => {
    const items = [];
    for (let page = 1; page <= MAX_PAGES; page++) {
      const data = await fetchPage(cfg.indexUrl, page);
      if (Array.isArray(data.items)) items.push(...data.items);
      if (!data.next || !data.items || data.items.length === 0) break;
    }
    writeCache(key, items);
    return items;
  })();
  indexPromise.catch(() => {
    indexPromise = null; // allow retry after a failed load
  });
  return indexPromise;
}

/* --------------------------------------------------------------- matching */

// Field weights (spec): title 10 (+15 whole-query == title), category 6,
// suburb/state 5, tags 4, summary 1.
const FIELDS = [
  { key: '_t', score: 10 },
  { key: '_c', score: 6 },
  { key: '_l', score: 5 },
  { key: '_g', score: 4 },
  { key: '_s', score: 1 },
];

function prepared(item) {
  if (!item._t) {
    item._t = tokenize(item.t);
    item._tn = item._t.join(' ');
    item._c = item.cat ? tokenize(item.cat.join(' ')) : [];
    item._l = tokenize(`${item.sub || ''} ${item.st || ''}`);
    item._g = item.tags ? tokenize(item.tags.join(' ')) : [];
    item._s = item.sum ? tokenize(item.sum) : [];
  }
  return item;
}

function tokenMatches(alias, fieldTokens) {
  const prefixOk = alias.length >= 3;
  for (const ft of fieldTokens) {
    if (ft === alias) return true;
    if (prefixOk && ft.length > alias.length && ft.startsWith(alias)) return true;
  }
  return false;
}

export function todaySydney() {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date());
}

/**
 * Run a search over the index.
 * @param {Array} items - index items
 * @param {string} query
 * @param {{scopeHandle?: string|null, state?: string|null, eventsScope?: boolean}} [opts]
 * @returns {Array} matched items, sorted
 */
export function searchIndex(items, query, opts = {}) {
  const qNorm = normalise(query);
  const qTokens = qNorm ? qNorm.split(' ') : [];
  if (!qTokens.length) return [];

  const today = todaySydney();
  const state = opts.state ? normalise(opts.state) : '';
  const results = [];

  // Pre-expand query tokens through the synonym map.
  const expanded = qTokens.map((t) => {
    const set = ALIASES.get(t);
    return set ? [t, ...[...set].filter((a) => a !== t)] : [t];
  });

  for (const raw of items) {
    const item = prepared(raw);

    // Past events never appear anywhere.
    if (item.date && item.date < today) continue;
    if (state && normalise(item.st || '') !== state) continue;
    if (opts.scopeHandle && (!item.cols || !item.cols.includes(opts.scopeHandle))) continue;

    let score = 0;
    let allMatched = true;
    for (const aliases of expanded) {
      let best = 0;
      for (const field of FIELDS) {
        if (best >= field.score) break; // fields are ordered by weight
        for (const alias of aliases) {
          if (tokenMatches(alias, item[field.key])) {
            best = field.score;
            break;
          }
        }
      }
      if (!best) {
        allMatched = false;
        break;
      }
      score += best;
    }
    if (!allMatched) continue;
    if (qNorm === item._tn) score += 15;
    results.push({ item, score });
  }

  if (opts.eventsScope) {
    // Events scope: dated events soonest first; undated after, by score.
    results.sort((a, b) => {
      const ad = a.item.date || '';
      const bd = b.item.date || '';
      if (ad && bd && ad !== bd) return ad < bd ? -1 : 1;
      if (ad && !bd) return -1;
      if (!ad && bd) return 1;
      return b.score - a.score || a.item.t.localeCompare(b.item.t);
    });
  } else {
    results.sort((a, b) => b.score - a.score || a.item.t.localeCompare(b.item.t));
  }

  return results.map((r) => r.item);
}

/* -------------------------------------------------------------- rendering */

// Initials tile — the canguaru-listing-thumb contract: colour by
// product.id / 65536 % 4 (QA20 measured deviation from plain modulo),
// Fraunces 900, aria-hidden. Events never get a tile (QA26).
const TILE_COLOURS = [
  ['#15453A', '#FBF7EF'],
  ['#2E3192', '#FBF7EF'],
  ['#D8491F', '#FBF7EF'],
  ['#FBF7EF', '#1E211D'],
];

const SVG_NS = 'http://www.w3.org/2000/svg';

export function buildTile(item, extraClass) {
  const [bg, fg] = TILE_COLOURS[Math.floor(item.id / 65536) % 4];
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', `cg-initials${extraClass ? ` ${extraClass}` : ''}`);
  svg.setAttribute('viewBox', '0 0 96 96');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('role', 'presentation');
  const rect = document.createElementNS(SVG_NS, 'rect');
  rect.setAttribute('width', '96');
  rect.setAttribute('height', '96');
  rect.setAttribute('fill', bg);
  const text = document.createElementNS(SVG_NS, 'text');
  text.setAttribute('x', '48');
  text.setAttribute('y', '50');
  text.setAttribute('text-anchor', 'middle');
  text.setAttribute('dominant-baseline', 'central');
  text.setAttribute('fill', fg);
  text.setAttribute('style', 'font-family: var(--cg-serif), Georgia, serif; font-weight: 900; font-size: 36px; letter-spacing: 0.01em;');
  text.textContent = item.init || '';
  svg.appendChild(rect);
  svg.appendChild(text);
  return svg;
}

function isEvent(item) {
  // Store reality: product.type is "Events" (plural); canguaru-listing-thumb
  // additionally catches singular 'Event' and any event_info.date.
  return item.type === 'Events' || item.type === 'Event' || Boolean(item.date);
}

function imgSrc(item) {
  // image_url emits protocol-relative URLs; force https so they also load
  // from the http theme-dev host.
  return item.img.startsWith('//') ? `https:${item.img}` : item.img;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function formatEventDate(iso) {
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d
      .toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
      .replace(/,/g, '')
      .toUpperCase();
  } catch (e) {
    return '';
  }
}

function metaLine(item) {
  return item.cat && item.cat.length ? item.cat.join(' · ') : '';
}

/** Full result card for the /search grid (li.cg-scard) — the collection
 * listing card, byte-for-byte in the visual sense: white box, 1px ink
 * border, 18px radius, square media (image, or the initials tile — on
 * /search EVERY type gets the tile when image-less, events included:
 * Gildo's search-cards brief, a scoped exception to QA26 text-first). */
function buildCard(item) {
  const li = el('li', 'cg-scard');
  const a = el('a', 'cg-scard__link');
  a.href = `/products/${item.h}`;

  const event = isEvent(item);
  const media = el(
    'div',
    `cg-scard__media${item.lg ? ' cg-scard__media--logo' : ''}${event && item.img ? ' cg-scard__media--event' : ''}`
  );
  if (item.img) {
    const img = document.createElement('img');
    img.src = imgSrc(item);
    img.alt = item.t;
    img.loading = 'lazy';
    media.appendChild(img);
  } else {
    media.appendChild(buildTile(item));
  }
  a.appendChild(media);

  a.appendChild(el('h3', 'cg-scard__title', item.t));

  const metaBox = el('div', 'cg-scard__meta');
  if (event && item.date) {
    metaBox.appendChild(el('span', 'cg-scard__m cg-scard__m--date', formatEventDate(item.date)));
  }
  const meta = metaLine(item);
  if (meta) metaBox.appendChild(el('span', 'cg-scard__m', meta));
  if (item.sub) metaBox.appendChild(el('span', 'cg-scard__m', `⌖ ${item.sub}${item.st ? ` ${item.st}` : ''}`));
  if (metaBox.children.length) a.appendChild(metaBox);

  li.appendChild(a);
  return li;
}

/** Compact popup row. */
function buildRow(item) {
  const a = el('a', 'cg-srow');
  a.href = `/products/${item.h}`;
  a.setAttribute('role', 'option');

  const event = isEvent(item);
  if (item.img) {
    const thumb = el('span', `cg-srow__thumb${item.lg ? ' cg-srow__thumb--logo' : ''}`);
    const img = document.createElement('img');
    img.src = imgSrc(item);
    img.alt = '';
    img.loading = 'lazy';
    thumb.appendChild(img);
    a.appendChild(thumb);
  } else if (!event && item.init) {
    const thumb = el('span', 'cg-srow__thumb');
    thumb.appendChild(buildTile(item));
    a.appendChild(thumb);
  } else {
    a.appendChild(el('span', 'cg-srow__thumb cg-srow__thumb--none'));
  }

  const body = el('span', 'cg-srow__body');
  body.appendChild(el('span', 'cg-srow__t', item.t));
  const bits = [];
  if (event && item.date) bits.push(formatEventDate(item.date));
  const meta = metaLine(item);
  if (meta) bits.push(meta);
  if (item.sub) bits.push(item.sub);
  if (bits.length) body.appendChild(el('span', 'cg-srow__m', bits.join(' · ')));
  a.appendChild(body);
  return a;
}

/* -------------------------------------------------------------- analytics */

function track(query, resultCount, scope, state) {
  if (resultCount === 0) console.debug('cg-search-zero', query);
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: 'search', query, results: resultCount, scope: scope || 'all', state: state || '' });
  }
}

/* ------------------------------------------------------------ search page */

const PAGE_SIZE = 24;

function initSearchPage(root) {
  const cfg = {
    indexUrl: root.dataset.indexUrl || '/pages/search-index?view=search-index',
    version: root.dataset.indexVersion || '1',
  };
  const scopeHandles = {
    food: root.dataset.scopeFood,
    events: root.dataset.scopeEvents,
    services: root.dataset.scopeServices,
  };
  const input = root.querySelector('[data-cg-search-input]');
  const form = root.querySelector('[data-cg-search-form]');
  const tabs = [...root.querySelectorAll('[data-cg-scope]')];
  const stateSelect = root.querySelector('[data-cg-state]');
  const countEl = root.querySelector('[data-cg-search-count]');
  const list = root.querySelector('[data-cg-search-results]');
  const moreBtn = root.querySelector('[data-cg-search-more]');
  const empty = root.querySelector('[data-cg-search-empty]');
  const emptyHeading = root.querySelector('[data-cg-search-empty-h]');
  const region = root.querySelector('[data-cg-search-results-region]');
  if (!input || !list) return;

  // Reserve region space immediately when the page loads with a query, so
  // the async render doesn't shift the footer (CLS).
  if (region && new URLSearchParams(location.search).get('q')) {
    region.setAttribute('data-cg-busy', '');
  }

  let scope = 'all';
  let state = '';
  let results = [];
  let shown = 0;

  function readUrl() {
    const params = new URLSearchParams(location.search);
    input.value = params.get('q') || input.value || '';
    const s = (params.get('scope') || 'all').toLowerCase();
    scope = scopeHandles[s] ? s : 'all';
    const st = (params.get('state') || '').toUpperCase();
    state = /^(NSW|VIC|QLD|WA|SA|ACT|TAS|NT)$/.test(st) ? st : '';
    syncControls();
  }

  function syncControls() {
    tabs.forEach((tab) => tab.setAttribute('aria-pressed', String(tab.dataset.cgScope === scope)));
    if (stateSelect) stateSelect.value = state;
  }

  function writeUrl(push) {
    const params = new URLSearchParams();
    const q = input.value.trim();
    // Keep the documented gate bypass param across client-side navigations.
    const preview = new URLSearchParams(location.search).get('preview');
    if (preview) params.set('preview', preview);
    if (q) params.set('q', q);
    if (scope !== 'all') params.set('scope', scope);
    if (state) params.set('state', state);
    const url = `${location.pathname}${params.toString() ? `?${params}` : ''}`;
    try {
      history[push ? 'pushState' : 'replaceState']({ cg: 1 }, '', url);
    } catch (e) {
      /* sandboxed preview */
    }
  }

  // "Show {n} more" — the {n} token in the section's more_label setting
  // becomes the next slice's size; a label without the token renders as-is.
  const morePattern = moreBtn ? moreBtn.textContent.trim() : '';
  const siteName = document.querySelector('meta[property="og:site_name"]')?.content || 'Canguaru';

  function renderSlice() {
    const slice = results.slice(shown, shown + PAGE_SIZE);
    for (const item of slice) list.appendChild(buildCard(item));
    shown += slice.length;
    if (!moreBtn) return;
    const remaining = results.length - shown;
    moreBtn.hidden = remaining <= 0;
    if (remaining > 0 && morePattern.includes('{n}')) {
      moreBtn.textContent = morePattern.replace('{n}', Math.min(PAGE_SIZE, remaining));
    }
  }

  function setTitle(text) {
    document.title = `${text} – ${siteName}`;
  }

  function showMessage(msg) {
    if (!countEl) return;
    countEl.hidden = false;
    countEl.textContent = msg;
  }

  async function run({ push = false, fromUrl = false } = {}) {
    if (!fromUrl) writeUrl(push);
    const q = input.value.trim();
    list.textContent = '';
    shown = 0;
    results = [];
    if (moreBtn) moreBtn.hidden = true;
    if (empty) empty.hidden = true;
    if (!q) {
      if (countEl) countEl.hidden = true;
      region?.removeAttribute('data-cg-busy');
      setTitle('Search');
      return;
    }
    let items;
    try {
      showMessage('Searching…');
      items = await loadIndex(cfg);
    } catch (e) {
      console.error('cg-search index load failed', e);
      showMessage('Search is unavailable right now — please try again.');
      region?.removeAttribute('data-cg-busy');
      return;
    }
    // A newer interaction may have started while the index loaded.
    if (input.value.trim() !== q) return;

    results = searchIndex(items, q, {
      scopeHandle: scope === 'all' ? null : scopeHandles[scope],
      state: state || null,
      eventsScope: scope === 'events',
    });
    track(q, results.length, scope, state);

    region?.removeAttribute('data-cg-busy');
    if (!results.length) {
      if (countEl) countEl.hidden = true;
      if (emptyHeading) emptyHeading.textContent = `No results for “${q}”`;
      if (empty) empty.hidden = false;
      setTitle(`No results for “${q}”`);
      return;
    }
    const countText = `${results.length} ${results.length === 1 ? 'result' : 'results'} for “${q}”`;
    showMessage(countText);
    setTitle(countText);
    renderSlice();
  }

  moreBtn?.addEventListener('click', () => renderSlice());

  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    run({ push: true });
  });

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      scope = tab.dataset.cgScope;
      syncControls();
      run({ push: true });
    });
  });

  stateSelect?.addEventListener('change', () => {
    state = stateSelect.value;
    run({ push: true });
  });

  root.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-cg-chip]');
    if (!chip) return;
    event.preventDefault();
    input.value = chip.textContent.trim();
    run({ push: true });
  });

  // Warm the index on first interaction.
  input.addEventListener('focus', () => loadIndex(cfg).catch(() => {}), { once: true });

  window.addEventListener('popstate', () => {
    readUrl();
    run({ fromUrl: true });
  });

  readUrl();
  if (input.value.trim()) run({ fromUrl: true });
}

/* ----------------------------------------------------------- header popup */

const POPUP_LIMIT = 6;
const POPUP_DEBOUNCE_MS = 150;

function initPopup(root) {
  const cfg = {
    indexUrl: root.dataset.indexUrl || '/pages/search-index?view=search-index',
    version: root.dataset.indexVersion || '1',
  };
  const input = root.querySelector('[data-cg-popup-input]');
  const resultsEl = root.querySelector('[data-cg-popup-results]');
  const resetBtn = root.querySelector('[data-cg-popup-reset]');
  if (!input || !resultsEl) return;

  let timer = null;
  let lastQuery = '';

  function clearResults() {
    resultsEl.textContent = '';
    resultsEl.hidden = true;
    input.setAttribute('aria-expanded', 'false');
  }

  function render(query, matched) {
    resultsEl.textContent = '';
    if (!matched.length) {
      track(query, 0, 'all', '');
      const none = el('p', 'cg-srow__none', `No results for “${query}”`);
      resultsEl.appendChild(none);
      resultsEl.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      return;
    }
    track(query, matched.length, 'all', '');
    for (const item of matched.slice(0, POPUP_LIMIT)) resultsEl.appendChild(buildRow(item));
    const all = el('a', 'cg-srow cg-srow--all', `See all ${matched.length} ${matched.length === 1 ? 'result' : 'results'}`);
    all.href = `/search?q=${encodeURIComponent(query)}`;
    resultsEl.appendChild(all);
    resultsEl.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  async function runPopup() {
    const query = input.value.trim();
    lastQuery = query;
    if (resetBtn) resetBtn.hidden = query.length === 0;
    if (!query) {
      clearResults();
      return;
    }
    let items;
    try {
      items = await loadIndex(cfg);
    } catch (e) {
      console.error('cg-search index load failed', e);
      clearResults();
      return;
    }
    if (lastQuery !== input.value.trim()) return;
    render(query, searchIndex(items, query, {}));
  }

  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = setTimeout(runPopup, POPUP_DEBOUNCE_MS);
  });
  input.addEventListener('focus', () => loadIndex(cfg).catch(() => {}), { once: true });

  resetBtn?.addEventListener('click', () => {
    input.value = '';
    resetBtn.hidden = true;
    clearResults();
    input.focus();
  });

  // Enter with no row focused submits the form to /search?q= (native).
}

/* ------------------------------------------------------------------- boot */

function boot() {
  document.querySelectorAll('[data-cg-search]').forEach(initSearchPage);
  document.querySelectorAll('[data-cg-search-popup]').forEach(initPopup);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
